import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageLayout from "../components/PageLayout";
import CtaBand from "../components/CtaBand";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Eyebrow, Reveal } from "../components/Reveal";
import { useContent } from "../content-context";
import { breadcrumb } from "../components/Seo";

function Answer({ text }) {
  const linkCls = "font-semibold text-gold underline underline-offset-4 transition-colors hover:text-navy";
  if (text.includes("Packages & Prices page")) {
    const idx = text.indexOf("Packages & Prices page");
    return (
      <>
        {text.slice(0, idx)}
        <Link to="/private-island-packages-prices/" data-testid="faq-packages-link" className={linkCls}>
          Packages &amp; Prices page
        </Link>
        {text.slice(idx + "Packages & Prices page".length)}
      </>
    );
  }
  if (text.includes("enquiry form")) {
    const idx = text.indexOf("enquiry form");
    return (
      <>
        {text.slice(0, idx)}
        <Link to="/contact/" data-testid="faq-contact-link" className={linkCls}>
          enquiry form
        </Link>
        {text.slice(idx + "enquiry form".length)}
      </>
    );
  }
  return <>{text}</>;
}

const SEO = {
  title: "Frequently Asked Questions | Collanmore Island Lodge, Clew Bay, Westport",
  description:
    "Answers about staying on Collanmore Island: ferry transfers from Rosmoney Pier (€20 per person), packages from €3,300, €1,000 deposit, activities, sauna, hot tub, catering, entertainment and Traitors Island.",
  path: "/frequently-asked-questions/",
};

export default function FaqsPage() {
  const { faqs } = useContent();
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: typeof f.a === "string" ? f.a : String(f.a) },
    })),
  };
  return (
    <PageLayout seo={{ ...SEO, jsonLd: [breadcrumb("/frequently-asked-questions/", "Frequently Asked Questions"), faqJsonLd] }}>
      <section className="relative overflow-hidden pt-28 md:pt-36">
        <div className="mx-auto max-w-[900px] px-5 pb-14 md:px-10">
          <Reveal>
            <Eyebrow>{faqs.heroEyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 font-display text-4xl font-medium leading-[1.05] text-navy md:text-6xl" data-testid="page-h1">
              {faqs.heroH1}
            </h1>
          </Reveal>
          {faqs.heroParas.map((p, i) => (
            <Reveal key={i} delay={0.14}>
              <p className="mt-5 max-w-2xl text-sm font-light leading-relaxed text-navy/75 md:text-base">{p}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-ivory pb-40 md:pb-32" data-testid="faq-list">
        <div className="mx-auto max-w-[900px] px-5 md:px-10">
          <Accordion type="single" collapsible className="border-t border-navy/10" data-testid="faq-accordion">
            {faqs.items.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i + 1}`} className="border-b border-navy/10">
                <AccordionTrigger className="text-left font-display text-lg text-navy hover:text-gold hover:no-underline md:text-xl">
                  {i + 1}. {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm font-light leading-relaxed text-navy/75 md:text-base">
                  <Answer text={f.a} />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-wrap items-center gap-4" data-testid="faq-links">
              <Link
                to="/private-island-packages-prices/"
                data-testid="faq-link-packages"
                className="inline-flex items-center gap-3 border border-navy px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-navy transition-colors duration-500 hover:bg-navy hover:text-ivory"
              >
                View Packages &amp; Prices
              </Link>
              <Link
                to="/contact/"
                data-testid="faq-link-contact"
                className="inline-flex items-center gap-3 bg-navy px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ivory transition-colors duration-500 hover:bg-gold hover:text-navy"
              >
                Check Availability <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand heading={faqs.ctaHeading} lead={faqs.ctaLead} cta={faqs.ctaLabel} to="/contact/" />
    </PageLayout>
  );
}
